import { useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Group,
  SupervisorAccount,
  Warning,
  Refresh,
  HelpOutline,
} from '@mui/icons-material';
import { usePermission, Permissions } from '@billda/permissions-ui';
import { useFeature, Features } from '@billda/features-ui';
import { PageBreadcrumb } from '../../../shared/components/layout/PageBreadcrumb';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { SidePanel } from '../../../shared/components/panel';
import { HelpPanel } from '../../../shared/components/help/HelpPanel';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog';
import { OFFBOARDING_HELP } from '../../../shared/constants/helpContent';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';
import { Routes, routeBuilders } from '../../../routes';
import { useCurrentUser } from '../../auth/hooks/useCurrentUser';
import { useUser } from '../hooks/useUser';
import { useOffboardReadiness } from '../hooks/useOffboardReadiness';
import { useStartOffboard } from '../hooks/useStartOffboard';
import { useCompleteOffboard } from '../hooks/useCompleteOffboard';
import { useCancelOffboard } from '../hooks/useCancelOffboard';
import { UserStatus } from '../api/userManagementApi';
import { getUserDisplayName } from '../utils/userDisplayUtils';
import { canManageUserOffboarding } from '../utils/roleChangePolicy';

const BLOCKER_ICONS: Record<string, React.ReactNode> = {
  ActivePots: <AccountBalanceWallet />,
  GangMembership: <Group />,
  GangLeadership: <SupervisorAccount />,
};

const BLOCKER_HINTS: Record<string, string> = {
  ActivePots:
    "This will be resolved when all of the user's pots are approved, paid, or archived",
  GangMembership: 'The user may not be a member of a gang',
  GangLeadership: 'The user may not be a leader of a gang',
};

function OffboardingPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading, error } = useUser(userId ?? null);
  const isFeatureEnabled = useFeature(Features.Offboarding.IsEnabled);
  const hasPermission = usePermission(Permissions.Users.Manage);
  const canManageAdmins = usePermission(Permissions.Users.ManageAdmins);
  const currentUser = useCurrentUser();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const startOffboard = useStartOffboard();
  const completeOffboard = useCompleteOffboard();
  const cancelOffboard = useCancelOffboard();

  const [helpOpen, setHelpOpen] = useState(false);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [revokeAccess, setRevokeAccess] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  if (!isFeatureEnabled || !hasPermission) {
    if (userId) {
      return <Navigate to={routeBuilders.users.detail(userId)} replace />;
    }
    return <Navigate to={Routes.users.manage} replace />;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
        data-testid="offboarding-page"
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
          <PageBreadcrumb
            segments={[
              { label: 'Users', to: Routes.users.manage },
              { label: 'Loading...' },
            ]}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
        data-testid="offboarding-page"
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
          <PageBreadcrumb
            segments={[
              { label: 'Users', to: Routes.users.manage },
              { label: 'Not Found' },
            ]}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {error ? 'Failed to load user details.' : 'User not found.'}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (
    user.status === UserStatus.Inactive ||
    user.status === UserStatus.InvitationSent
  ) {
    return <Navigate to={routeBuilders.users.detail(userId!)} replace />;
  }

  const isCurrentUser = !!(currentUser && user.id === currentUser.userId);
  if (
    !canManageUserOffboarding({
      roles: user.roles,
      isCurrentUser,
      canManageAdmins,
    })
  ) {
    return <Navigate to={routeBuilders.users.detail(userId!)} replace />;
  }

  const displayName = getUserDisplayName(user);

  const handleStartOffboard = () => {
    startOffboard.mutate(
      { userId: userId!, revokeAccess },
      {
        onSuccess: () => {
          setStartDialogOpen(false);
          setRevokeAccess(false);
          showSnackbar('Offboarding started', 'success');
        },
      }
    );
  };

  const handleCompleteOffboard = () => {
    completeOffboard.mutate(userId!, {
      onSuccess: () => {
        setCompleteDialogOpen(false);
        showSnackbar('Offboarding complete', 'success');
        navigate(routeBuilders.users.detail(userId!));
      },
    });
  };

  const handleCancelOffboard = () => {
    cancelOffboard.mutate(userId!, {
      onSuccess: () => {
        setCancelDialogOpen(false);
        showSnackbar('Offboarding cancelled', 'success');
        navigate(routeBuilders.users.detail(userId!));
      },
    });
  };

  return (
    <PageContentArea
      isPanelOpen={helpOpen}
      panel={
        helpOpen ? (
          <SidePanel
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
            title="Help"
            testId="offboarding-help-drawer"
          >
            <HelpPanel helpContent={OFFBOARDING_HELP} />
          </SidePanel>
        ) : undefined
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
        data-testid="offboarding-page"
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            pt: 2,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <PageBreadcrumb
            segments={[
              { label: 'Users', to: Routes.users.manage },
              {
                label: displayName,
                to: routeBuilders.users.detail(userId!),
              },
              { label: 'Offboarding' },
            ]}
          />
          <IconButton
            size="small"
            onClick={() => setHelpOpen(true)}
            data-testid="offboarding-help-button"
            sx={{ color: 'action.active' }}
          >
            <HelpOutline sx={{ fontSize: '1.5rem' }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: { xs: 2, sm: 3 },
            py: 3,
            maxWidth: 720,
          }}
        >
          {user.status === UserStatus.Active && (
            <ActiveSection
              displayName={displayName}
              startDialogOpen={startDialogOpen}
              revokeAccess={revokeAccess}
              isPending={startOffboard.isPending}
              onOpenDialog={() => setStartDialogOpen(true)}
              onCloseDialog={() => {
                setStartDialogOpen(false);
                setRevokeAccess(false);
              }}
              onRevokeAccessChange={setRevokeAccess}
              onConfirm={handleStartOffboard}
            />
          )}

          {user.status === UserStatus.Offboarding && (
            <OffboardingSection
              userId={userId!}
              displayName={displayName}
              completeDialogOpen={completeDialogOpen}
              cancelDialogOpen={cancelDialogOpen}
              isCompletePending={completeOffboard.isPending}
              isCancelPending={cancelOffboard.isPending}
              onOpenCompleteDialog={() => setCompleteDialogOpen(true)}
              onCloseCompleteDialog={() => setCompleteDialogOpen(false)}
              onOpenCancelDialog={() => setCancelDialogOpen(true)}
              onCloseCancelDialog={() => setCancelDialogOpen(false)}
              onConfirmComplete={handleCompleteOffboard}
              onConfirmCancel={handleCancelOffboard}
            />
          )}
        </Box>
      </Box>
    </PageContentArea>
  );
}

interface ActiveSectionProps {
  displayName: string;
  startDialogOpen: boolean;
  revokeAccess: boolean;
  isPending: boolean;
  onOpenDialog: () => void;
  onCloseDialog: () => void;
  onRevokeAccessChange: (value: boolean) => void;
  onConfirm: () => void;
}

function ActiveSection({
  displayName,
  startDialogOpen,
  revokeAccess,
  isPending,
  onOpenDialog,
  onCloseDialog,
  onRevokeAccessChange,
  onConfirm,
}: ActiveSectionProps) {
  return (
    <>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Starting offboarding will replace {displayName}&apos;s current role with
        a restricted Offboarding role. They will still be able to submit pots
        and view invoices, but will be unable to book new work or manage teams.
      </Typography>

      <Button
        variant="contained"
        color="error"
        onClick={onOpenDialog}
        data-testid="offboarding-start-button"
      >
        Start Offboarding
      </Button>

      <Dialog
        open={startDialogOpen}
        onClose={onCloseDialog}
        data-testid="offboarding-start-dialog"
      >
        <DialogTitle>Start offboarding {displayName}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Their current role will be replaced with a restricted Offboarding
            role. They&apos;ll only be able to submit pots, resubmit rejected
            work, and view invoices. Other actions like booking new work will be
            blocked.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={revokeAccess}
                onChange={e => onRevokeAccessChange(e.target.checked)}
                data-testid="offboarding-revoke-access-checkbox"
              />
            }
            label="Log user out now"
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 4, mt: 0.5 }}
          >
            Log the user out immediately. Without this, the changes can take up
            to 15 minutes to take effect.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseDialog}>Cancel</Button>
          <Button
            onClick={onConfirm}
            color="error"
            variant="contained"
            disabled={isPending}
          >
            Start Offboarding
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

interface OffboardingSectionProps {
  userId: string;
  displayName: string;
  completeDialogOpen: boolean;
  cancelDialogOpen: boolean;
  isCompletePending: boolean;
  isCancelPending: boolean;
  onOpenCompleteDialog: () => void;
  onCloseCompleteDialog: () => void;
  onOpenCancelDialog: () => void;
  onCloseCancelDialog: () => void;
  onConfirmComplete: () => void;
  onConfirmCancel: () => void;
}

function OffboardingSection({
  userId,
  displayName,
  completeDialogOpen,
  cancelDialogOpen,
  isCompletePending,
  isCancelPending,
  onOpenCompleteDialog,
  onCloseCompleteDialog,
  onOpenCancelDialog,
  onCloseCancelDialog,
  onConfirmComplete,
  onConfirmCancel,
}: OffboardingSectionProps) {
  const { data: readiness, isLoading, refetch } = useOffboardReadiness(userId);

  const getBlockerIcon = (type: string) => {
    return BLOCKER_ICONS[type] ?? <Warning />;
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Readiness
        </Typography>
        <IconButton
          onClick={() => refetch()}
          data-testid="offboarding-readiness-refresh"
          size="small"
        >
          <Refresh />
        </IconButton>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {readiness && readiness.blockers.length > 0 && (
        <List data-testid="offboarding-blocker-list" sx={{ mb: 3 }}>
          {readiness.blockers.map((blocker, index) => (
            <ListItem key={index} data-testid="offboarding-blocker-item">
              <ListItemIcon>{getBlockerIcon(blocker.type)}</ListItemIcon>
              <ListItemText
                primary={blocker.description}
                secondary={BLOCKER_HINTS[blocker.type]}
              />
            </ListItem>
          ))}
        </List>
      )}

      {readiness && readiness.blockers.length === 0 && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          data-testid="offboarding-ready-indicator"
        >
          Ready to complete offboarding
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {readiness?.canComplete ? (
          <Button
            variant="contained"
            color="error"
            onClick={onOpenCompleteDialog}
            disabled={isCompletePending}
            data-testid="offboarding-complete-button"
          >
            Complete Offboarding
          </Button>
        ) : (
          <Tooltip title="Resolve all blockers before completing offboarding">
            <span>
              <Button
                variant="contained"
                color="error"
                disabled
                data-testid="offboarding-complete-button"
              >
                Complete Offboarding
              </Button>
            </span>
          </Tooltip>
        )}

        <Button
          variant="outlined"
          color="warning"
          onClick={onOpenCancelDialog}
          disabled={isCancelPending}
          data-testid="offboarding-cancel-button"
        >
          Cancel Offboarding
        </Button>
      </Box>

      <ConfirmDialog
        open={completeDialogOpen}
        title={`Complete offboarding for ${displayName}?`}
        message="This will remove all access to the organisation. Their gang memberships, site assignments, and roles will be removed. Historical records (pots, invoices, bookings) are preserved. This cannot be undone — to restore access, you would need to re-invite them."
        onConfirm={onConfirmComplete}
        onCancel={onCloseCompleteDialog}
        confirmText="Complete Offboarding"
      />

      <ConfirmDialog
        open={cancelDialogOpen}
        title={`Cancel offboarding for ${displayName}?`}
        message="They will be restored to their previous role and regain full access. Any work they were restricted from will become available again."
        onConfirm={onConfirmCancel}
        onCancel={onCloseCancelDialog}
        confirmText="Cancel Offboarding"
      />
    </>
  );
}

export default OffboardingPage;
